import type { ComponentProps } from "react";
import { Fragment } from "react";
import { jt, t } from "ttag";

import { env } from "@/config/env";
import type { SelectedProduct } from "@/features/products/context";
import {
  BILLING_FREQUENCY,
  DISCOUNT_DURATION,
  PRICE_TAG_TYPE,
  PRICE_TYPE,
} from "@/shared/domains";
import { Badge, DetailCard, RadioGroup, Text } from "@/shared/ui";
import { formatPrice, getBillingFrequencyVerbose } from "@/shared/utils";

const PRICE_TAG_COLOR = {
  [PRICE_TAG_TYPE.INFORMATION]: "green",
  [PRICE_TAG_TYPE.SAVE]: "light-primary",
  [PRICE_TAG_TYPE.ADMINISTRATION_METHOD]: "light-primary",
  [PRICE_TAG_TYPE.AVAILABILITY_INFORMATION]: "light-primary",
  [PRICE_TAG_TYPE.PAYMENT_TYPE]: "transparent",
  [PRICE_TAG_TYPE.DISABLED_REASON]: "light-primary",
} as const;

interface PriceSelectorProps extends ComponentProps<typeof RadioGroup.Root> {
  options: SelectedProduct["prices"];
  klarnaDisabled?: boolean;
  showDiscount?: boolean;
}

export const PriceSelector = ({
  options,
  klarnaDisabled = false,
  showDiscount = false,
  ...props
}: PriceSelectorProps) => {
  const hasSinglePrice = options.length === 1;

  if (hasSinglePrice) {
    return null;
  }

  const klarnaStrong = <strong key="klarna-strong">Klarna</strong>;
  const klarnaDisclaimer = jt`${klarnaStrong} available for 3, 6, or 12 mo. plans.`;

  return (
    <RadioGroup.Root isCard {...props}>
      {options.map(
        ({
          id,
          price,
          durationInMonths,
          tags,
          type,
          isSubscribed,
          discount,
        }) => {
          const isMonthlyPlan = durationInMonths === BILLING_FREQUENCY.MONTHLY;

          const recurringFrequency =
            getBillingFrequencyVerbose(durationInMonths);

          const isSelected = Number(props.value) === id;

          const activeDiscount =
            showDiscount && discount?.isValid ? discount : null;
          const effectivePrice = activeDiscount?.priceAfterDiscount ?? price;

          const BILLING_DESCRIPTION = {
            [PRICE_TYPE.ONE_TIME]: t`You will be billed $${price}`,
            [PRICE_TYPE.RECURRING]: t`You will be billed $${price}, every ${recurringFrequency} until you cancel`,
          } as const;

          const billingDescription =
            activeDiscount?.duration === DISCOUNT_DURATION.ONCE &&
            type === PRICE_TYPE.RECURRING
              ? t`You will be billed $${effectivePrice} today, then $${price} every ${recurringFrequency} until you cancel`
              : BILLING_DESCRIPTION[type];

          const hasLongerPlan = options.some(
            (option) => option.durationInMonths > durationInMonths,
          );

          return (
            <Fragment key={id}>
              <RadioGroup.Item value={String(id)} disabled={isSubscribed}>
                <div className="grid grid-cols-[1fr_auto] grid-rows-[auto_auto] gap-x-2 sm:grid-rows-1">
                  <RadioGroup.Label className="row-start-1 grow font-semibold">
                    {activeDiscount && (
                      <>
                        <Text
                          size="small"
                          weight="normal"
                          color="light-neutral"
                          lineThrough
                        >
                          {formatPrice(price / durationInMonths)}
                        </Text>{" "}
                      </>
                    )}
                    {formatPrice(effectivePrice / durationInMonths)}

                    <Text size="small" weight="normal">
                      {t` / mo `}
                      {durationInMonths > 1 && t`(${durationInMonths} mo plan)`}
                    </Text>
                  </RadioGroup.Label>

                  {!isMonthlyPlan && !isSubscribed && (
                    <div className="row-start-2 sm:row-start-1 sm:self-center">
                      <Text as="p" size="small" color="dark-neutral">
                        {formatPrice(effectivePrice)}{" "}
                        <Text size="x-small" color="dark-neutral">
                          {t`today`}
                        </Text>
                      </Text>
                    </div>
                  )}

                  <div className="row-span-full row-start-1 flex flex-col items-end gap-2 sm:flex-row sm:items-center">
                    {tags.map(({ type, label }) =>
                      type === PRICE_TAG_TYPE.PAYMENT_TYPE ? (
                        <Text
                          key={label}
                          as="p"
                          size="small"
                          color="dark-neutral"
                        >
                          {label}
                        </Text>
                      ) : (
                        <Badge
                          key={label}
                          size="x-small"
                          color={PRICE_TAG_COLOR[type]}
                        >
                          {label}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              </RadioGroup.Item>

              {isSelected && (
                <DetailCard.Description className="p-3 text-neutral-700 sm:text-sm">
                  {billingDescription}
                </DetailCard.Description>
              )}

              {isMonthlyPlan &&
                type === PRICE_TYPE.RECURRING &&
                hasLongerPlan && (
                  <DetailCard.Description className="p-2 text-neutral-700 sm:text-base">
                    {t`Stay committed, save more — longer plans, better value.`}{" "}
                    {env.VITE_KLARNA_ENABLED &&
                      !klarnaDisabled &&
                      klarnaDisclaimer}
                  </DetailCard.Description>
                )}
            </Fragment>
          );
        },
      )}
    </RadioGroup.Root>
  );
};
